import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'action');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const filteredVideos = useMemo(
    () => videos.filter((v) => v.categorySlug === activeSlug),
    [activeSlug]
  );

  const categoryTitle = currentCategory?.name || '影视';
  const categoryDesc = currentCategory?.description || '';

  return (
    <>
      <SEOHead
        title={`${categoryTitle} - 麻豆影视在线观看 | 免费${categoryTitle}全集高清在线播放`}
        description={`麻豆影视在线观看${categoryTitle}频道，${categoryDesc}所有${categoryTitle}资源免费在线观看，高清流畅不缓冲。`}
        keywords={`${categoryTitle},麻豆影视在线观看,在线看${categoryTitle},免费${categoryTitle},${categoryTitle}在线播放`}
        canonicalUrl={`https://www.madouys.com/category/${activeSlug}`}
      />
      <SchemaOrg type="WebPage" data={{ name: `${categoryTitle} - 麻豆影视`, url: `https://www.madouys.com/category/${activeSlug}`, isPartOf: { '@type': 'WebSite', name: '麻豆影视', url: 'https://www.madouys.com/' } }} />
      <SchemaOrg type="BreadcrumbList" data={{ itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.madouys.com/' },
        { '@type': 'ListItem', position: 2, name: categoryTitle, item: `https://www.madouys.com/category/${activeSlug}` },
      ] }} />

      <ScrollToTop />
      <Navbar />

      <main className="pt-24 pb-10 md:pb-14">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb items={[{ label: categoryTitle }]} />
          </div>

          {/* Category Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              {categoryTitle}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-[700px]">
              {categoryDesc}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-4 mb-8 scrollbar-hide flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveSlug(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Video Grid */}
          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4" data-product-shop>
              {filteredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-movie-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}